import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

/**
 * Enforces MFA on protected portal pages by checking:
 * - user is authenticated
 * - profile reports MFA is enabled
 * - current auth session has completed MFA verification
 */
export function useRequireMfa() {
  const { user, loading, isSessionMfaVerified, session, signOut } = useAuth();
  const [checking, setChecking] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  const isExpired = () => {
    if (!session?.expires_at) return false;
    const expiresAtSeconds = typeof session.expires_at === 'number'
      ? session.expires_at
      : parseInt(session.expires_at as unknown as string, 10);
    return expiresAtSeconds * 1000 <= Date.now();
  };

  useEffect(() => {
    if (loading) return;

    if (!user) {
      navigate('/portal/login', { replace: true, state: { from: location.pathname } });
      setChecking(false);
      return;
    }

    const verifyMfa = async () => {
      try {
        // If the session is expired/stale, sign out and go back to auth
        if (isExpired()) {
          await signOut();
          navigate('/portal/login', { replace: true });
          return;
        }

        const { data, error } = await supabase
          .from('profiles')
          .select('mfa_method')
          .eq('user_id', user.id)
          .maybeSingle();

        const hasMfaEnabled = !!data?.mfa_method;
        if (error) {
          console.error('MFA check failed:', error);
        }

        if (!hasMfaEnabled) {
          navigate('/portal/login', { replace: true });
          return;
        }

        // If we can't confirm this existing session has passed MFA, force a full re-login.
        // This avoids prompting for MFA on a previously-authenticated session (e.g. shared devices).
        if (!isSessionMfaVerified()) {
          await signOut();
          navigate('/portal/login', { replace: true });
          return;
        }

        setChecking(false);
      } catch (err) {
        console.error('Unexpected MFA check error:', err);
        navigate('/portal/login', { replace: true });
      }
    };

    verifyMfa();
  }, [loading, user, isSessionMfaVerified, navigate, location.pathname, signOut, session?.expires_at]);

  return { checking: loading || checking };
}
